
import React from 'react';
import type { VoteContext } from '../governanceService';

interface VoteContextHeaderProps {
    context: VoteContext;
    isCreator: boolean;
    onClose: () => void;
    onRefresh: () => void;
}


export const VoteContextHeader: React.FC<VoteContextHeaderProps> = ({
    context,
    isCreator,
    onClose,
    onRefresh
}) => {
    // DIAGNOSTIC LOG (TEMPORARY)
    console.log('[VoteContextHeader] context raw:', context);

    // DEFENSIVE: Options normalization
    let safeOptions: string[] = [];
    if (context?.options) {
        if (Array.isArray(context.options)) {
            safeOptions = context.options;
        } else if (typeof context.options === 'string') {
            try {
                // Try parsing JSON string array
                const parsed = JSON.parse(context.options);
                if (Array.isArray(parsed)) {
                    safeOptions = parsed;
                } else {
                    safeOptions = [String(context.options)];
                }
            } catch (e) {
                // Fallback for plain string
                safeOptions = [String(context.options)];
            }
        } else {
            // Unknown type (object/number?), force string array
            safeOptions = [String(context.options)];
        }
    }
    console.log('[VoteContextHeader] safeOptions:', safeOptions);

    const getStatusColor = (status: string) => {
        switch (status) {
            case 'OPEN': return 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400';
            case 'CLOSED': return 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-400';
            case 'TIED': return 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400';
            case 'EXPIRED': return 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400';
            default: return 'bg-slate-100 text-slate-700';
        }
    };

    const getTimeRemaining = () => {
        if (!context?.expires_at) return 'Unknown';

        try {
            const now = new Date();
            const expires = new Date(context.expires_at);

            if (isNaN(expires.getTime())) return 'Invalid Date';

            const diff = expires.getTime() - now.getTime();

            if (diff <= 0) return 'Expired';

            const hours = Math.floor(diff / (1000 * 60 * 60));
            const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));

            if (hours > 24) {
                const days = Math.floor(hours / 24);
                return `${days}d ${hours % 24}h`;
            }
            return `${hours}h ${minutes}m`;
        } catch (e) {
            return 'Error';
        }
    };

    return (
        <div className="mb-4 p-4 bg-slate-50 dark:bg-slate-900 rounded-lg border border-slate-200 dark:border-slate-700">
            <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                    <span className="text-xl">🗳️</span>
                    <h3 className="font-bold text-slate-800 dark:text-slate-200">
                        {context.context_type} Voting
                    </h3>
                </div>
                <div className="flex items-center gap-2">
                    <button
                        onClick={onRefresh}
                        className="p-1 text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 transition-colors"
                        title="Refresh Now"
                    >
                        🔄
                    </button>
                    <button
                        onClick={onClose}
                        className="p-1 text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 transition-colors"
                        title="Close"
                    >
                        ✕
                    </button>
                </div>
            </div>

            <div className="space-y-2 text-sm">
                <div className="flex items-center gap-2">
                    <span className="text-slate-500 dark:text-slate-400">Status:</span>
                    <span className={`px-2 py-0.5 rounded text-xs font-bold ${getStatusColor(context.status)}`}>
                        {context.status}
                    </span>
                </div>

                {context.status === 'OPEN' && (
                    <div className="flex items-center gap-2">
                        <span className="text-slate-500 dark:text-slate-400">Expires:</span>
                        <span className="text-slate-700 dark:text-slate-300 font-medium">
                            {getTimeRemaining()}
                        </span>
                    </div>
                )}

                <div className="flex items-center gap-2">
                    <span className="text-slate-500 dark:text-slate-400">Options:</span>
                    <span className="text-slate-700 dark:text-slate-300 font-mono text-xs">
                        {safeOptions.join(', ')}
                    </span>
                </div>
            </div>

            {isCreator && context.status === 'OPEN' && (
                <button
                    onClick={onClose}
                    className="mt-3 w-full px-3 py-2 text-sm font-medium text-red-600 border border-red-300 rounded hover:bg-red-50 dark:text-red-400 dark:border-red-800 dark:hover:bg-red-900/20 transition-colors"
                >
                    Close Voting
                </button>
            )}
        </div>
    );
};
